import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  cancelCircuitRun,
  createCircuit,
  deleteCircuit,
  fetchCircuit,
  fetchCircuits,
  rotateCircuitWebhook,
  triggerCircuit,
  updateCircuit,
} from "@/lib/api";
import type {
  CircuitDefinition,
  CircuitCronOverlapPolicy,
  CircuitListOwnerScope,
  CircuitStatus,
  CircuitTriggerConfig,
  ManagedOwnerScope,
} from "@/lib/types";

export function useCircuits(
  ownerScope: CircuitListOwnerScope = "personal",
  status?: CircuitStatus,
) {
  return useQuery({
    queryKey: ["circuits", ownerScope, status],
    queryFn: () => fetchCircuits(ownerScope, status),
  });
}

export function useCircuit(id: string | null) {
  return useQuery({
    queryKey: ["circuit", id],
    queryFn: () => fetchCircuit(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const active = query.state.data?.runs.some((run) =>
        ["pending", "running", "waiting", "cancelling"].includes(run.status),
      );
      return active ? 3_000 : false;
    },
  });
}

type CircuitFormData = {
  name: string;
  cronOverlapPolicy?: CircuitCronOverlapPolicy;
  description?: string;
  definition: CircuitDefinition;
  triggers: CircuitTriggerConfig[];
};

export function useCreateCircuit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ownerScope, ...data }: CircuitFormData & { ownerScope: ManagedOwnerScope }) =>
      createCircuit(data, ownerScope),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["circuits"] }),
  });
}

export function useUpdateCircuit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: Partial<CircuitFormData> & { id: string; status?: CircuitStatus }) =>
      updateCircuit(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["circuits"] });
      queryClient.invalidateQueries({ queryKey: ["circuit", variables.id] });
    },
  });
}

export function useDeleteCircuit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCircuit,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["circuits"] });
      queryClient.removeQueries({ queryKey: ["circuit", id], exact: true });
    },
  });
}

export function useTriggerCircuit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: triggerCircuit,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["circuits"] });
      queryClient.invalidateQueries({ queryKey: ["circuit", id] });
    },
  });
}

export function useRotateCircuitWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, webhookId }: { id: string; webhookId: string }) =>
      rotateCircuitWebhook(id, webhookId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["circuit", variables.id] });
    },
  });
}

export function useCancelCircuitRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, runId }: { id: string; runId: string }) => cancelCircuitRun(id, runId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["circuit", variables.id] });
    },
  });
}
